/**
 * DepartureFormBuilder — create form for Departure Form report.
 * Mirrors the Daily Sale Update pattern: fill form → preview → save as draft.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const HEALTH_CATEGORIES = ['Medical', 'Daily Care', 'Maintenance', 'Conditioning'];

export default function DepartureFormBuilder({ onGenerated }) {
  const [horses, setHorses]         = useState([]);
  const [horseId, setHorseId]       = useState('');
  const [travels, setTravels]       = useState([]);
  const [travelId, setTravelId]     = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState(() => new Date().toLocaleDateString('sv'));
  const [categories, setCategories] = useState(['Medical', 'Daily Care', 'Maintenance', 'Conditioning']);
  const [loading, setLoading]       = useState(false);
  const [loadingHorses, setLoadingHorses] = useState(true);

  useEffect(() => {
    api.get('/reports/departure-form/horses')
      .then(r => setHorses(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingHorses(false));
  }, []);

  useEffect(() => {
    if (!horseId) { setTravels([]); setTravelId(''); return; }
    api.get(`/reports/departure-form/travel/${horseId}`)
      .then(r => { setTravels(r.data || []); setTravelId(''); })
      .catch(() => {});
  }, [horseId]);

  const toggleCategory = (cat) => {
    setCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleGenerate = async () => {
    if (!horseId) { toast.error('Please select a horse'); return; }
    if (!travelId) { toast.error('Please select a travel event'); return; }
    setLoading(true);
    try {
      const res = await api.post('/reports/departure-form/run', {
        horse_id: horseId,
        travel_scheduled_care_id: travelId,
        health_date_from: dateFrom,
        health_date_to: dateTo,
        health_categories: categories,
      });
      onGenerated(res.data);
    } catch {
      toast.error('Failed to generate departure form');
    }
    setLoading(false);
  };

  const selectedTravel = travels.find(t => t.id === travelId);
  const travelLabel = (t) => {
    const dir = t.travel_direction === 'from_farm' ? 'Leaving Farm' : t.travel_direction === 'to_farm' ? 'Returning' : 'Travel';
    const dest = t.travel_destination_name || t.travel_dest_other || '';
    const rawDate = t.start_date || t.next_due_date || '';
    const rawTime = t.preferred_time || '';
    let dateLabel = '';
    if (rawDate) {
      try {
        dateLabel = new Date(rawDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch { dateLabel = rawDate; }
    }
    let timeLabel = '';
    if (rawTime) {
      try {
        const [h, m] = rawTime.split(':').map(Number);
        timeLabel = ` @ ${h % 12 || 12}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
      } catch { timeLabel = ''; }
    }
    return `${dir}${dest ? ` → ${dest}` : ''}${dateLabel ? ` · ${dateLabel}${timeLabel}` : ''}`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Departure Form</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Generate a departure document from scheduled travel.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Horse */}
        <div className="space-y-1">
          <Label className="font-semibold text-sm">Horse</Label>
          {loadingHorses ? (
            <p className="text-xs text-muted-foreground">Loading horses…</p>
          ) : (
            <Select value={horseId || '__none__'} onValueChange={v => setHorseId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select horse…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {horses.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.system_name || h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {horseId && horses.length > 0 && travels.length === 0 && (
            <p className="text-xs text-muted-foreground italic mt-1">No scheduled travel found for this horse. Schedule a travel event first.</p>
          )}
        </div>

        {/* Travel event */}
        {horseId && travels.length > 0 && (
          <div className="space-y-1">
            <Label className="font-semibold text-sm">Travel Event</Label>
            <Select value={travelId || '__none__'} onValueChange={v => setTravelId(v === '__none__' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select travel…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Select —</SelectItem>
                {travels.map(t => (
                  <SelectItem key={t.id} value={t.id}>{travelLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Health Records date range */}
        <div className="space-y-1">
          <Label className="font-semibold text-sm">Health Records Date Range</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">From</p>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className={dateFrom ? 'time-filled' : ''} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">To</p>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className={dateTo ? 'time-filled' : ''} />
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-2">
          <Label className="font-semibold text-sm">Health Record Categories</Label>
          <div className="space-y-1.5">
            {HEALTH_CATEGORIES.map(cat => (
              <label key={cat} className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={categories.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  className="rounded"
                />
                {cat}
              </label>
            ))}
          </div>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={loading || !horseId || !travelId}
          className="w-full bg-primary hover:bg-primary/90 text-white">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : 'Generate Departure Form'}
        </Button>
      </CardContent>
    </Card>
  );
}
